import moment from 'moment';
import FileSaver from 'file-saver';

import { generateEmployeesAssetsPDF } from 'lib/pdfs/EmployeesAssetsPDF';
import { generateEmployeesAssetsPricePDF } from 'lib/pdfs/EmployeesAssetsPricePDF';
import { generateUnitaryVoucherPDF } from 'lib/pdfs/EmployeesAssetUnitarioPDF';
import { generateUnitaryVoucherPricePDF } from 'lib/pdfs/EmployeesAssetUnitarioPricePDF';
import {
  autosurCustodyVoucherBlob,
  custodyVoucherFileName,
  downloadAutosurCustodyVoucher,
  locationName,
  signerName,
} from 'lib/pdfs/autosur';
import { FORMATS_WITH_PRICE, VOUCHER_FORMATS, voucherConfig } from 'lib/vouchers/clientConfig';
import { printable } from 'lib/pdfs/autosur/valeResguardoAutosur';

export {
  VOUCHER_FORMATS,
  canSignGeneralVoucher,
  detailVoucherOptions,
  voucherConfig,
} from 'lib/vouchers/clientConfig';
export { custodianChanged, needsNewVoucherNotice, voucherStatus } from 'lib/vouchers/custodian';

export const STAMP_FORMAT = 'YYYY-MM-DD_HHmm';

const fullName = (employee) =>
  employee ? [employee.name, employee.lastname].filter(Boolean).join(' ') : '';

const valueOf = (ref, field) => {
  const v = Array.isArray(ref) ? ref[0] : ref;
  return (v && typeof v === 'object' ? v[field] : v) || '';
};

/**
 * One asset of the detail as the record the employee vouchers expect: the same
 * fields reports_service.employeesAssetsPaginated groups (employee in the root,
 * the assets in `assets`), so a client format prints one asset exactly as it
 * prints them in Reportes > Vales de resguardo. Pure.
 */
export const employeeRecordFromAsset = (asset) => {
  const employee = asset._employee || {};
  const record = {
    id: employee.id || employee._id,
    key: employee.key,
    name: employee.name,
    lastname: employee.lastname,
    position: employee.position,
    email: employee.email,
    total: 1,
    assets: [
      {
        id: asset.id || asset._id,
        name: asset.name,
        number: asset.number,
        description: asset.description,
        billNumber: asset.billNumber,
        buyDate: asset.buyDate,
        billDate: asset.billDate,
        physicalState: valueOf(asset._physicalState, 'value'),
        account: valueOf(asset._account, 'name'),
        subaccount: valueOf(asset._subaccount, 'name'),
        serie: asset.serie,
        model: asset.model,
        brand: asset.brand,
        price: asset.price,
        exchange: asset.exchange,
      },
    ],
  };
  // The report leaves imgUrl out when the employee has no photo, and Círculo
  // checks `record.imgUrl !== undefined` to fall back to the blank picture.
  if (employee.imgUrl !== undefined) record.imgUrl = employee.imgUrl;
  return record;
};

/**
 * The location catalog of the asset detail (AssetsFormConfig: the parents, and
 * one list of sublocations per level of the asset's path) as one list.
 */
export const locationCatalog = (locations, sublocations) =>
  (sublocations || []).reduce((all, level) => all.concat(level || []), [].concat(locations || []));

/**
 * One asset of the detail as the record the AUTOSUR voucher prints: the report
 * record plus what that format adds, the location as names (from the location
 * catalog of the detail, ctx.locations) and the custody notes. Pure.
 */
export const autosurRecordFromAsset = (asset, ctx = {}) => {
  const record = employeeRecordFromAsset(asset);
  return {
    ...record,
    assets: [
      {
        ...record.assets[0],
        locationName: locationName(asset.location, ctx.locations),
        custodyNotes: asset.custodyNotes,
      },
    ],
  };
};

/**
 * vale_resguardo_{employee key}_{YYYY-MM-DD_HHmm}.pdf, the shape the back accepts
 * (CUSTODY_VOUCHER_NAME), for the voucher of all the employee's assets.
 */
export const employeeVoucherFileName = (record, stamp) =>
  `vale_resguardo_${(printable(record && record.key) || 'empleado')
    .replace(/[^A-Za-z0-9_-]+/g, '-')
    .slice(0, 120)}_${stamp}.pdf`;

/** What the formats print: {image, signedAt, generatedBy, custodian}. */
export const voucherSignature = (image, user, now, custodian) => ({
  image,
  signedAt: now.format('DD/MM/YYYY HH:mm'),
  generatedBy: signerName(user),
  custodian,
});

const byEmployee = (asset, ctx, withPrice, options) =>
  (withPrice ? generateEmployeesAssetsPricePDF : generateEmployeesAssetsPDF)(
    employeeRecordFromAsset(asset),
    ctx.subcompany,
    options
  );

const unitary = (asset, ctx, withPrice, options) =>
  (withPrice ? generateUnitaryVoucherPricePDF : generateUnitaryVoucherPDF)(
    { asset, subcompany: ctx.subcompany, t: ctx.t },
    options
  );

/**
 * Per-asset vouchers the signing modal can produce. AUTOSUR has its own builder
 * (lib/pdfs/autosur, one document per employee) and its HTML preview; the
 * others reuse the format they already had, with one asset.
 */
const hasPrice = (format) => FORMATS_WITH_PRICE.includes(format);

export const INDIVIDUAL_FORMATS = {
  [VOUCHER_FORMATS.GENERIC]: { label: 'Genérico', price: hasPrice('generico'), run: unitary },
  [VOUCHER_FORMATS.CIRCULO_CREDITO]: {
    label: 'Círculo de Crédito',
    price: hasPrice('circulo_credito'),
    run: byEmployee,
  },
  [VOUCHER_FORMATS.CIRCULO_LABORAL]: {
    label: 'Círculo Laboral',
    price: hasPrice('circulo_laboral'),
    run: byEmployee,
  },
  [VOUCHER_FORMATS.AUTOSUR]: { label: 'AutoSur', price: hasPrice('autosur') },
};

export const individualFormat = (format) => {
  const found = INDIVIDUAL_FORMATS[format];
  if (!found) throw new Error(`Unknown custody voucher format: ${format}`);
  return found;
};

const downloadAutosurIndividual = (asset, ctx) =>
  downloadAutosurCustodyVoucher(
    autosurRecordFromAsset(asset, ctx),
    ctx,
    custodyVoucherFileName(asset, moment().format(STAMP_FORMAT))
  );

/**
 * "Acciones > Exportar PDF" (with or without price) in the client's own format:
 * opened in a new tab, as the generic vouchers of that menu do. AutoSur's
 * builder only downloads (as its former "Vale de resguardo" button did).
 * @param {Object} ctx - {company, subcompany, t, user (session user), locations
 *   (location catalog of the detail, for AutoSur)}
 */
export const exportIndividualVoucher = (format, asset, ctx, { withPrice = false } = {}) => {
  if (format === VOUCHER_FORMATS.AUTOSUR) return downloadAutosurIndividual(asset, ctx);
  return individualFormat(format).run(asset, ctx, withPrice, {});
};

/**
 * After "Firmar y guardar": the user also gets the signed PDF (user's decision
 * of 2026-11-02). Called only once the back saved it.
 */
export const saveSignedCopy = (blob, fileName) => FileSaver.saveAs(blob, fileName);

/**
 * "Descargar para firma en papel".
 * @param {Object} ctx - as exportIndividualVoucher
 */
export const downloadIndividualVoucher = (format, asset, ctx, { withPrice = false } = {}) => {
  if (format === VOUCHER_FORMATS.AUTOSUR) return downloadAutosurIndividual(asset, ctx);
  const fileName = custodyVoucherFileName(asset, moment().format(STAMP_FORMAT));
  return individualFormat(format).run(asset, ctx, withPrice, { download: fileName });
};

/**
 * The signed voucher as a Blob, ready for Api.assets.addCustodyVoucher. Date and
 * time are the signer's local ones (what is printed); the back stores its own.
 * @returns {Promise<{blob: Blob, fileName: string}>}
 */
export const signIndividualVoucher = async (
  format,
  asset,
  ctx,
  signatureImage,
  user,
  { withPrice = false } = {}
) => {
  const now = moment();
  const signature = voucherSignature(signatureImage, user, now, fullName(asset._employee));
  const blob =
    format === VOUCHER_FORMATS.AUTOSUR
      ? await autosurCustodyVoucherBlob(
          autosurRecordFromAsset(asset, ctx),
          { ...ctx, user },
          signature
        )
      : await individualFormat(format).run(asset, ctx, withPrice, { signature, blob: true });
  return { blob, fileName: custodyVoucherFileName(asset, now.format(STAMP_FORMAT)) };
};

const employeeGenerator = (withPrice) =>
  withPrice ? generateEmployeesAssetsPricePDF : generateEmployeesAssetsPDF;

// AutoSur's format also covers the employee voucher (user's decision of
// 2026-11-03); the other clients get theirs from EmployeesAssets[Price]PDF,
// which picks it by subcompany.
const isAutosurGeneral = (ctx) =>
  voucherConfig(ctx.company, ctx.subcompany).individualFormat === VOUCHER_FORMATS.AUTOSUR;

/** Whether the employee voucher of this client exists with price. */
export const generalVoucherHasPrice = (company, subcompany) =>
  !isAutosurGeneral({ company, subcompany });

/**
 * Reportes > Vales de resguardo, "Exportar PDF" and "Exportar PDF con precio"
 * of each employee: as always (opened in a new tab), except AutoSur, whose
 * format has no price and is downloaded.
 * @param {Object} record - a row of the employees_assets report
 * @param {Object} ctx - {company, subcompany, user (session user)}
 */
export const exportGeneralVoucher = (record, ctx, { withPrice = false } = {}) =>
  isAutosurGeneral(ctx)
    ? downloadAutosurCustodyVoucher(
        record,
        ctx,
        employeeVoucherFileName(record, moment().format(STAMP_FORMAT))
      )
    : employeeGenerator(withPrice)(record, ctx.subcompany);

/**
 * Reportes > Vales de resguardo, "Descargar para firma en papel": the client's
 * format of the employee voucher, without signature.
 * @param {Object} record - a row of the employees_assets report
 * @param {Object} ctx - as exportGeneralVoucher
 */
export const downloadGeneralVoucher = (record, ctx, { withPrice = false } = {}) => {
  const fileName = employeeVoucherFileName(record, moment().format(STAMP_FORMAT));
  return isAutosurGeneral(ctx)
    ? downloadAutosurCustodyVoucher(record, ctx, fileName)
    : employeeGenerator(withPrice)(record, ctx.subcompany, { download: fileName });
};

/**
 * The signed employee voucher as a Blob, for Api.assets.addEmployeeCustodyVoucher.
 * @returns {Promise<{blob: Blob, fileName: string}>}
 */
export const signGeneralVoucher = async (
  record,
  ctx,
  signatureImage,
  user,
  { withPrice = false } = {}
) => {
  const now = moment();
  const signature = voucherSignature(signatureImage, user, now, fullName(record));
  const blob = isAutosurGeneral(ctx)
    ? await autosurCustodyVoucherBlob(record, { ...ctx, user }, signature)
    : await employeeGenerator(withPrice)(record, ctx.subcompany, { signature, blob: true });
  return { blob, fileName: employeeVoucherFileName(record, now.format(STAMP_FORMAT)) };
};



// WEBPACK FOOTER //
// src/lib/vouchers/index.js